import React, { useState } from "react";
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Button,
  Input,
  Textarea,
  Heading,
  Badge,
  Image,
  Link,
} from "@chakra-ui/react";
import homeOffice from "../utils/images/homeOffice.png";
import ContactButton from '../utils/ContactButton';
import { contactService } from '../firebase/contactService.js';

const ContactSection = () => {
  const contact = {
    phone: process.env.REACT_APP_PHONE || "+33 4 01 00 11 18",
    email: process.env.REACT_APP_EMAIL || "contact@groupe-solarenov.com",
    workingHours: process.env.REACT_APP_WORKING_HOURS || "Lun–Ven 9:00–18:00",
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Le nom est requis";
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Format d'email invalide";
    if (!formData.service.trim()) newErrors.service = "Veuillez sélectionner un service";
    if (!formData.message.trim()) newErrors.message = "Le message est requis";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    
    try {
      const contactData = {
        firstName: formData.name.split(' ')[0] || formData.name,
        lastName: formData.name.split(' ').slice(1).join(' ') || '',
        phone: formData.phone,
        email: formData.email,
        service: formData.service,
        message: formData.message,
        source: 'contact-form'
      };

      // Validation avec le service
      const validation = contactService.validateContact(contactData);
      if (!validation.isValid) {
        setNotification({
          status: 'error',
          title: 'Erreur de validation',
          description: validation.errors.join(', ')
        });
        return;
      }

      // Envoyer à Firestore
      await contactService.addContact(contactData);

      // Envoyer l'email de confirmation (non bloquant)
      contactService.sendConfirmationEmail(contactData);
      
      // Succès
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setNotification({
        status: 'success',
        title: 'Message envoyé !',
        description: 'Nous vous répondrons dans les plus brefs délais.'
      });
      
      // Masquer la notification après 5 secondes
      setTimeout(() => setNotification(null), 5000);
      
    } catch (error) {
      console.error('Erreur:', error);
      setNotification({
        status: 'error',
        title: 'Erreur',
        description: 'Une erreur est survenue. Veuillez réessayer.'
      });
      
      // Masquer la notification après 5 secondes
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      as="section"
      py={{ base: 20, lg: 28 }}
      bg="linear-gradient(135deg, #094c26 0%, #0d6b35 50%, #063a1d 100%)"
      position="relative"
      id="contact"
    >
      {/* Background Pattern Overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="radial-gradient(circle at 30px 30px, rgba(255,255,255,0.08) 2px, transparent 2px)"
        backgroundSize="60px 60px"
        opacity={0.3}
      />
      <Container maxW="1200px" px={5} position="relative" zIndex={1}>
        {notification && (
          <Box
            bg={notification.status === 'success' ? 'rgba(34, 197, 94, 0.95)' : 'rgba(239, 68, 68, 0.95)'}
            color="white"
            borderRadius="2xl"
            p={6}
            mb={8}
            backdropFilter="blur(10px)"
            border="1px solid"
            borderColor="whiteAlpha.300"
          >
            <VStack spacing={2} align="center" w="100%">
              <Text fontSize="4xl">{notification.status === 'success' ? '✅' : '❌'}</Text>
              <Text fontSize="lg" fontWeight={700}>
                {notification.title}
              </Text>
              <Text fontSize="md">
                {notification.description}
              </Text>
            </VStack>
          </Box>
        )}

        {/* Header Section */}
        <VStack spacing={6} textAlign="center" mb={{ base: 12, lg: 16 }}>
          <VStack spacing={3}>
            <Heading
              as="h2"
              size="xl"
              fontWeight={800}
              color="white"
              lineHeight="1.2"
            >
              Contactez nos experts
            </Heading>
            <Text
              fontSize="md"
              color="white"
              maxW="2xl"
              lineHeight="1.6"
              fontWeight={500}
              opacity={0.95}
            >
              Demandez votre devis gratuit et personnalisé. Un conseiller vous rappelle sous 24h pour étudier votre projet.
            </Text>
          </VStack>
        </VStack>

        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
          gap={{ base: 10, lg: 16 }}
          alignItems="start"
        >
          {/* Left Column - Contact Info */}
          <GridItem>
            <VStack spacing={6} align="start" h="100%">
              {/* Contact Cards */}
              <VStack spacing={4} w="100%" flex="1">
                {/* Contact Button Card */}
                <Box
                  bg="rgba(255, 255, 255, 0.15)"
                  color="white"
                  borderRadius="2xl"
                  p={6}
                  w="100%"
                  backdropFilter="blur(15px)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 20px 40px rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <HStack spacing={3} align="start">
                    <Box
                      w="48px"
                      h="48px"
                      bg="rgba(255, 255, 255, 0.2)"
                      borderRadius="xl"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      backdropFilter="blur(10px)"
                    >
                      <Text fontSize="xl">💬</Text>
                    </Box>
                    <VStack align="start" spacing={1} flex="1">
                      <Text
                        fontSize="xs"
                        fontWeight={600}
                        opacity={0.9}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Contact
                      </Text>
                      <ContactButton />
                    </VStack>
                  </HStack>
                </Box>

                {/* Email Card */}
                <Box
                  bg="rgba(255, 255, 255, 0.15)"
                  color="white"
                  borderRadius="2xl"
                  p={6}
                  w="100%"
                  backdropFilter="blur(15px)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 20px 40px rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <HStack spacing={3} align="start">
                    <Box
                      w="48px"
                      h="48px"
                      bg="rgba(255, 255, 255, 0.2)"
                      borderRadius="xl"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      backdropFilter="blur(10px)"
                    >
                      <Text fontSize="xl">✉️</Text>
                    </Box>
                    <VStack align="start" spacing={1} flex="1">
                      <Text
                        fontSize="xs"
                        fontWeight={600}
                        opacity={0.9}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Email
                      </Text>
                      <Link
                        href={`mailto:${contact.email}`}
                        fontSize="lg"
                        fontWeight={700}
                        color="white"
                        _hover={{ opacity: 0.8 }}
                        transition="opacity 0.2s"
                      >
                        {contact.email}
                      </Link>
                    </VStack>
                  </HStack>
                </Box>

                {/* Hours Card */}
                <Box
                  bg="rgba(255, 255, 255, 0.15)"
                  color="white"
                  borderRadius="2xl"
                  p={6}
                  w="100%"
                  backdropFilter="blur(15px)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 20px 40px rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <HStack spacing={3} align="start">
                    <Box
                      w="48px"
                      h="48px"
                      bg="rgba(255, 255, 255, 0.2)"
                      borderRadius="xl"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      backdropFilter="blur(10px)"
                    >
                      <Text fontSize="xl">⏰</Text>
                    </Box>
                    <VStack align="start" spacing={1} flex="1">
                      <Text
                        fontSize="xs"
                        fontWeight={600}
                        opacity={0.9}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Horaires
                      </Text>
                      <Text fontSize="lg" fontWeight={700}>
                        {contact.workingHours}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              </VStack>

              {/* Office Location Card */}
              <Box
                w="100%"
                h="320px"
                borderRadius="2xl"
                overflow="hidden"
                position="relative"
                bg="rgba(255, 255, 255, 0.1)"
                backdropFilter="blur(15px)"
                border="1px solid"
                borderColor="whiteAlpha.300"
              >
                <Image
                  src={homeOffice}
                  alt="Nos bureaux"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  position="absolute"
                  top={0}
                  left={0}
                  zIndex={0}
                />
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  bg="linear-gradient(transparent, rgba(0, 0, 0, 0.8))"
                  p={6}
                  color="white"
                  zIndex={1}
                >
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Box
                        w="32px"
                        h="32px"
                        bg="rgba(255, 255, 255, 0.2)"
                        borderRadius="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        backdropFilter="blur(10px)"
                      >
                        <Text fontSize="md">📍</Text>
                      </Box>
                      <Text fontWeight={700} fontSize="md">
                        Nos bureaux
                      </Text>
                    </HStack>
                    <Text fontSize="sm" opacity={0.9} fontWeight={500}>
                      276 avenue du Prado
                      <br />
                      13008 Marseille
                    </Text>
                  </VStack>
                </Box>
              </Box>
            </VStack>
          </GridItem>

          {/* Right Column - Contact Form */}
          <GridItem>
            <Box
              bg="rgba(255, 255, 255, 0.12)"
              borderRadius="2xl"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
              p={{ base: 6, lg: 8 }}
              backdropFilter="blur(20px)"
              border="1px solid"
              borderColor="whiteAlpha.400"
              color="white"
              h="100%"
            >
              <VStack spacing={6} align="start">
                <VStack align="start" spacing={3}>
                  <Heading size="md" color="white" fontWeight={800}>
                    Envoyez-nous un message
                  </Heading>
                  <Text
                    color="white"
                    fontSize="sm"
                    fontWeight={500}
                    opacity={0.9}
                  >
                    Remplissez le formulaire ci-dessous et nous vous répondrons
                    rapidement.
                  </Text>
                </VStack>

                <Box as="form" w="100%" onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    {/* Name Field */}
                    <Box w="100%">
                      <Text
                        fontSize="xs"
                        fontWeight={600}
                        color="white"
                        mb={2}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Nom complet *
                      </Text>
                      <Input
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        placeholder="Votre nom complet"
                        size="md"
                        borderRadius="xl"
                        bg="rgba(255, 255, 255, 0.1)"
                        border="1px solid"
                        borderColor={errors.name ? "red.400" : "whiteAlpha.300"}
                        color="white"
                        _placeholder={{ color: "white" }}
                        _focus={{
                          borderColor: "white",
                          bg: "rgba(255, 255, 255, 0.15)",
                          boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                        }}
                        _hover={{ bg: "rgba(255, 255, 255, 0.12)" }}
                      />
                      {errors.name && (
                        <Text
                          color="red.300"
                          fontSize="sm"
                          mt={2}
                          fontWeight={500}
                        >
                          {errors.name}
                        </Text>
                      )}
                    </Box>

                    {/* Email Field */}
                    <Box w="100%">
                      <Text
                        fontSize="sm"
                        fontWeight={600}
                        color="white"
                        mb={3}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Email *
                      </Text>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        placeholder="votre@email.com"
                        size="lg"
                        borderRadius="xl"
                        bg="rgba(255, 255, 255, 0.1)"
                        border="1px solid"
                        borderColor={
                          errors.email ? "red.400" : "whiteAlpha.300"
                        }
                        color="white"
                        _placeholder={{ color: "white" }}
                        _focus={{
                          borderColor: "white",
                          bg: "rgba(255, 255, 255, 0.15)",
                          boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                        }}
                        _hover={{ bg: "rgba(255, 255, 255, 0.12)" }}
                      />
                      {errors.email && (
                        <Text
                          color="red.300"
                          fontSize="sm"
                          mt={2}
                          fontWeight={500}
                        >
                          {errors.email}
                        </Text>
                      )}
                    </Box>

                    {/* Phone Field */}
                    <Box w="100%">
                      <Text
                        fontSize="sm"
                        fontWeight={600}
                        color="white"
                        mb={3}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Téléphone
                      </Text>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        placeholder="Votre numéro de téléphone"
                        size="lg"
                        borderRadius="xl"
                        bg="rgba(255, 255, 255, 0.1)"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        color="white"
                        _placeholder={{ color: "white" }}
                        _focus={{
                          borderColor: "white",
                          bg: "rgba(255, 255, 255, 0.15)",
                          boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                        }}
                        _hover={{ bg: "rgba(255, 255, 255, 0.12)" }}
                      />
                    </Box>

                    {/* Service Field */}
                    <Box w="100%">
                      <Text
                        fontSize="sm"
                        fontWeight={600}
                        color="white"
                        mb={3}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Type de projet *
                      </Text>
                      <Box
                        as="select"
                        value={formData.service}
                        onChange={(e) =>
                          handleInputChange("service", e.target.value)
                        }
                        size="lg"
                        borderRadius="xl"
                        bg="rgba(255, 255, 255, 0.1)"
                        border="1px solid"
                        borderColor={errors.service ? "red.400" : "whiteAlpha.300"}
                        color="white"
                        w="100%"
                        h="48px"
                        px={4}
                        fontSize="md"
                        fontWeight={500}
                        _focus={{
                          borderColor: "white",
                          bg: "rgba(255, 255, 255, 0.15)",
                          boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                          outline: "none",
                        }}
                        _hover={{ bg: "rgba(255, 255, 255, 0.12)" }}
                        sx={{
                          "& option": {
                            bg: "#1a202c",
                            color: "white",
                          },
                        }}
                      >
                        <option value="" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Sélectionnez votre projet
                        </option>
                        <option value="Pompe à chaleur" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Pompe à chaleur
                        </option>
                        <option value="Isolation thermique" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Isolation thermique
                        </option>
                        <option value="Panneaux solaires" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Panneaux solaires photovoltaïques
                        </option>
                        <option value="Chauffe-eau solaire" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Chauffe-eau solaire
                        </option>
                        <option value="Ventilation VMC" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Ventilation (VMC)
                        </option>
                        <option value="Rénovation globale" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Rénovation globale
                        </option>
                        <option value="Autre" style={{ backgroundColor: "#1a202c", color: "white" }}>
                          Autre projet
                        </option>
                      </Box>
                      {errors.service && (
                        <Text
                          color="red.300"
                          fontSize="sm"
                          mt={2}
                          fontWeight={500}
                        >
                          {errors.service}
                        </Text>
                      )}
                    </Box>

                    {/* Message Field */}
                    <Box w="100%">
                      <Text
                        fontSize="sm"
                        fontWeight={600}
                        color="white"
                        mb={3}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Message *
                      </Text>
                      <Textarea
                        value={formData.message}
                        onChange={(e) =>
                          handleInputChange("message", e.target.value)
                        }
                        placeholder="Décrivez votre projet (surface, type de logement, année de construction...)"
                        size="lg"
                        borderRadius="xl"
                        bg="rgba(255, 255, 255, 0.1)"
                        border="1px solid"
                        borderColor={
                          errors.message ? "red.400" : "whiteAlpha.300"
                        }
                        color="white"
                        _placeholder={{ color: "white" }}
                        _focus={{
                          borderColor: "white",
                          bg: "rgba(255, 255, 255, 0.15)",
                          boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                        }}
                        _hover={{ bg: "rgba(255, 255, 255, 0.12)" }}
                        rows={5}
                        resize="vertical"
                      />
                      {errors.message && (
                        <Text
                          color="red.300"
                          fontSize="sm"
                          mt={2}
                          fontWeight={500}
                        >
                          {errors.message}
                        </Text>
                      )}
                    </Box>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      bg="rgba(255, 255, 255, 0.2)"
                      color="white"
                      size="lg"
                      w="100%"
                      isLoading={isSubmitting}
                      loadingText="Envoi en cours..."
                      borderRadius="xl"
                      fontWeight={600}
                      fontSize="md"
                      py={6}
                      backdropFilter="blur(10px)"
                      border="1px solid"
                      borderColor="whiteAlpha.300"
                      _hover={{
                        bg: "rgba(255, 255, 255, 0.3)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                      }}
                      _active={{ transform: "translateY(0)" }}
                      transition="all 0.3s ease"
                    >
                      Envoyer le message
                    </Button>
                  </VStack>
                </Box>

                <Text
                  fontSize="xs"
                  color="white"
                  fontWeight={500}
                  textAlign="center"
                  w="100%"
                  opacity={0.8}
                >
                  En soumettant ce formulaire, vous acceptez notre politique de
                  confidentialité.
                </Text>
              </VStack>
            </Box>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactSection;
